import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  ChangeDetectorRef,
  Component, Injectable, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewEncapsulation,
} from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';


export class ItemFullNode {
  name: string;
  Id: number;
  parentId: any;
  children: ItemFullNode[];
  isChecked: boolean;
  topMostParentName: string;
  nodeType: 'SINGLE' | 'GROUP_NODE' | 'GROUP_LEAF' | 'GROUP_PARENT';
}

export class ItemFlatNode {
  expandable: boolean;
  name: string;
  Id: number;
  parentId: any;
  level: number;
  isChecked: boolean;
  topMostParentName: string;
  nodeType: 'SINGLE' | 'GROUP_NODE' | 'GROUP_LEAF' | 'GROUP_PARENT';
}

@Component({
  selector: 'app-tree-checklist',
  templateUrl: 'tree-checklist.component.html',
  styleUrls: ['tree-checklist.component.scss'],
  // encapsulation: ViewEncapsulation.None,
})

export class TreeChecklistComponent implements OnInit, OnChanges {
  @Input() label: string;
  @Input() error: string;
  @Input() color: string;
  @Input() dataset: any[];
  @Input() existingData: any[];
  @Input() showItems: boolean;
  @Output() shareCheckedList: EventEmitter<string[]> = new EventEmitter<string[]>();

  requirementsToList: any;
  showDropDown: boolean;
  checkedList: any[];
  checkedIds: any[];
  matches: any[];

  //Map from flat node to nested node. This helps us finding the nested node to be modified
  FlatNodeMap = new Map<ItemFlatNode, ItemFullNode>();

  //Map from nested node to flattened node. This helps us to keep the same object for selection
  nestedNodeMap = new Map<ItemFullNode, ItemFlatNode>();

  //A selected parent node to be inserted
  selectedParent: ItemFlatNode | null = null;

  //The selection for checklist
  checklistSelection = new SelectionModel<ItemFlatNode>(true /* multiple */);

  treeControl: FlatTreeControl<ItemFlatNode>;

  treeFlattener: MatTreeFlattener<ItemFullNode, ItemFlatNode>;

  dataSource: MatTreeFlatDataSource<ItemFullNode, ItemFlatNode>;

  constructor(private cdr: ChangeDetectorRef) {
    this.checkedList = [];
    this.checkedIds = [];

    this.treeControl = new FlatTreeControl<ItemFlatNode>(this.getLevel, this.isExpandable);

    this.treeFlattener = new MatTreeFlattener(
      this._transformer, this.getLevel, this.isExpandable, this.getChildren,
    );

    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  }

  ngOnInit() {
    this.updateDataSource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataset'] && this.dataSource) {
      this.updateDataSource();
    }
  }

  private updateDataSource() {
    if (this?.dataset?.length) {
      this.dataSource.data = this.dataset.slice().sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
      this.matches = [];
      setTimeout(() => {
        if (this?.treeControl?.dataNodes?.length) {
          this.treeControl.expandAll();
          if (this.showDropDown) {
            this.preSetCheckboxes();
          }
        }
        this.cdr.detectChanges();
      });
    }
  }

  getLevel = (node: ItemFlatNode) => node.level;

  isExpandable = (node: ItemFlatNode) => node.expandable;

  getChildren = (node: ItemFullNode): ItemFullNode[] => node.children;

  hasChild = (_: number, _nodeData: ItemFlatNode) => _nodeData.expandable;

  hasNoContent = (_: number, _nodeData: ItemFlatNode) => _nodeData.name === '';

  // Transformer to convert nested node to flat node. Record the nodes in maps for later use.
  private _transformer = (node: ItemFullNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.Id === node.Id
      ? existingNode
      : new ItemFlatNode();
    flatNode.Id = node.Id;
    flatNode.level = level;
    flatNode.name = node.name;
    flatNode.parentId = node.parentId;
    flatNode.topMostParentName = node.topMostParentName;
    flatNode.nodeType = node.nodeType;
    flatNode.isChecked = this.checkedChecker(node);
    flatNode.expandable = !!node.children && node.children.length > 0;
    this.FlatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  }
  //Whether all the descendants of the node are selected
  descendantsAllSelected(node: ItemFlatNode): boolean {

    const descendants = this.treeControl.getDescendants(node);

    if (descendants.length === 0) {
      return this.checklistSelection.isSelected(node);
    }
    const allselected = descendants.every(child => this.checklistSelection.isSelected(child));
    if (allselected) {
      this.checklistSelection.select(node);
    }

    return allselected;

  }

  //Whether part of the descendants are selected
  descendantsPartiallySelected(node: ItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some(child => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  //Toggle the to-do item selection. Select/deselect all the descendants node
  ItemSelectionToggle(node: ItemFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);

    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);

    this.syncCheckedCollectionsAndEmit(true);
  }

  //Clear all checkboxes
  uncheckAll() {
    for (let i = 0; i < this.treeControl.dataNodes.length; i++) {
      if (this.checklistSelection.isSelected(this.treeControl.dataNodes[i])) {
        this.checklistSelection.deselect(this.treeControl.dataNodes[i]);
      }
    }
  }

  formatCheckedList() {
    return this.checkedList.sort().join(', ');
  }


  //Set checkboxes to match existing selections
  preSetCheckboxes() {
    this.matches = [];
    //clear the checks first
    this.uncheckAll();

    // Check if dataset exists and is an array
    if (!this.dataset || !Array.isArray(this.dataset) || this.dataset.length === 0) {
      return;
    }

    const walk = (nodes: any[] = []) => {
      nodes.forEach((node: any) => {
        if (node && this.checkedChecker(node)) {
          this.matches.push(node);
        }
        if (Array.isArray(node?.children) && node.children.length > 0) {
          walk(node.children);
        }
      });
    };

    walk(this.dataset);

    const idsToSelect = new Set(this.matches.map((item: any) => Number(item?.Id)));
    const nodesToSelect = (this.treeControl?.dataNodes || []).filter((node) => idsToSelect.has(Number(node.Id)));
    if (nodesToSelect.length > 0) {
      this.checklistSelection.select(...nodesToSelect);
    }
    this.syncCheckedCollectionsAndEmit(false);

  }

  //Share the data and clear the selected lists
  shareCheckedlist(list: string[]) {
    if (list.length > 0) {
      this.shareCheckedList.emit(list);
      this.checkedIds = [];
      this.uncheckAll();
      this.showDropDown = false;
    }

  }

  onClick() {
    if (this.showDropDown) {
      this.showDropDown = false;
    }
    else {
      this.preSetCheckboxes();
      this.showDropDown = true;
    }
  }

  //TODO need to resolve incoming requirementId to make more generalized
  //Check the checkbox if previously selected
  checkedChecker = (item: any) => {
    return this?.existingData.filter((existingItem: any) => existingItem.requirementId === item.Id).length > 0;
  }

  private syncCheckedCollectionsAndEmit(emit: boolean): void {
    const selectedValues = this.checklistSelection.selected;
    const filteredSelectedValues = selectedValues.filter((node) => {
      const isSingle = node.nodeType === 'SINGLE';
      const isLeaf = node.nodeType === 'GROUP_LEAF';
      const isGroupNode = node.nodeType === 'GROUP_NODE';
      const isGroupParent = node.nodeType === 'GROUP_PARENT';
      if (isSingle || isLeaf || isGroupNode || isGroupParent) {
        return true;
      }

      const hasSelectedChildren = selectedValues.some((selectedItem) => selectedItem.parentId === node.Id);
      return hasSelectedChildren;
    });

    this.checkedList = [];
    this.checkedIds = [];

    const resultName = new Set(filteredSelectedValues.flatMap((node) => node.topMostParentName));
    const resultId = selectedValues.flatMap((node) => node.Id);
    if (this.showItems) {
      this.checkedList.push(...resultName);
    }
    this.checkedIds.push(resultId);
    if (emit) {
      this.shareCheckedList.emit(this.checkedIds);
    }
  }
}
